/**
 * @file UserAvatar - Profile picture with automatic initials fallback.
 *
 * Displays a user's avatar image with a multi-step fallback chain:
 *   1. Explicit `src` prop (highest priority)
 *   2. `user.profileImageUrl` from the Firestore user document
 *   3. Computed initials from user name fields or `name` prop
 *   4. "?" as the last resort when no identity info is available
 *
 * MUI Avatar handles the image-to-children fallback natively: if `src`
 * fails to load, the `children` (initials or icon) are rendered instead.
 *
 * Commonly used in list rows, cards, and the app header. Wrap with
 * `onClick` for navigable avatars (e.g., linking to a user profile).
 *
 * @example
 *   <UserAvatar user={employee} size={48} hoverEffect />
 *   <UserAvatar name="Max Mustermann" bgcolor="#1e62d0" onClick={goToProfile} />
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box } from '@mui/material';

/**
 * Extract up to two initials from a user object.
 *
 * Handles multiple field-naming conventions found across the codebase:
 *   - prename/firstname and lastname/lastName (Firestore user docs)
 *   - displayName (Firebase Auth profile)
 *
 * @param {object|null} user
 * @returns {string} One or two uppercase characters, or "?" if user is null
 */
const getInitials = (user) => {
  if (!user) return '?';

  const prename = user.prename || user.firstName || '';
  const lastname = user.lastname || user.lastName || '';

  // If we have a displayName, try to extract initials from it
  if (!prename && !lastname && user.displayName) {
    const parts = user.displayName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return user.displayName.charAt(0).toUpperCase();
  }

  return `${prename.charAt(0)}${lastname.charAt(0)}`.toUpperCase();
};

/**
 * UserAvatar - displays profile picture with initials fallback
 *
 * @param {Object} props
 * @param {Object} props.user - User object with profileImageUrl, prename, lastname
 * @param {string} props.src - Direct image URL (overrides user.profileImageUrl)
 * @param {string} props.name - Fallback name for initials (if no user object)
 * @param {number|string} props.size - Avatar size (default: 40)
 * @param {Object} props.sx - Additional MUI sx styles
 * @param {string} props.bgcolor - Background color for initials fallback
 * @param {string} props.border - Border style (e.g., '2px solid #4caf50')
 * @param {ReactNode} props.icon - Icon to show instead of initials
 * @param {function} props.onClick - Click handler
 * @param {boolean} props.hoverEffect - Enable hover animation
 */
const UserAvatar = ({
  user,
  src,
  name,
  size = 40,
  sx = {},
  bgcolor,
  border,
  icon,
  onClick,
  hoverEffect = false,
  ...props
}) => {
  // Determine image source
  const imageSrc = src || user?.profileImageUrl || null;

  // Determine initials
  let initials = '?';
  if (user) {
    initials = getInitials(user);
  } else if (name) {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      initials = `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    } else {
      initials = name.charAt(0).toUpperCase();
    }
  }

  // Build sx object with optional hover effects.
  // Font size scales at 40% of avatar size so initials stay proportional.
  const avatarSx = {
    width: size,
    height: size,
    fontSize: size * 0.4,
    bgcolor: bgcolor,
    border: border,
    cursor: onClick ? 'pointer' : undefined,
    ...(hoverEffect && {
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: 2,
      }
    }),
    ...sx
  };

  // Avatar content: icon takes precedence, then initials
  const avatarContent = icon || initials;

  const avatar = (
    <Avatar
      src={imageSrc}
      alt={user?.displayName || name || 'User'}
      sx={avatarSx}
      {...props}
    >
      {avatarContent}
    </Avatar>
  );

  // Wrapping in a <Box component="span"> ensures the click target covers the
  // full avatar area and keeps the element inline for use inside table cells.
  if (onClick) {
    return (
      <Box
        component="span"
        onClick={onClick}
        sx={{ display: 'inline-flex', cursor: 'pointer' }}
      >
        {avatar}
      </Box>
    );
  }

  return avatar;
};

UserAvatar.propTypes = {
  user: PropTypes.shape({
    profileImageUrl: PropTypes.string,
    prename: PropTypes.string,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    displayName: PropTypes.string
  }),
  src: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
  bgcolor: PropTypes.string,
  border: PropTypes.string,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  hoverEffect: PropTypes.bool
};

UserAvatar.defaultProps = {
  user: null,
  src: null,
  name: null,
  size: 40,
  sx: {},
  bgcolor: undefined,
  border: undefined,
  icon: null,
  onClick: null,
  hoverEffect: false
};

export default UserAvatar;
